import { allowCancellations, cancelable } from "@mirage/shared/util/cancelable";
import { useAtomValue, useSetAtom } from "jotai";
import { useCallback, useEffect } from "react";
import { getDashTrialNotificationsLastSeen } from "..";
import {
  lastSeenChatNotificationAtom,
  lastSeenConnectorNotificationAtom,
  lastSeenOmniNotificationAtom,
  lastSeenSerpNotificationAtom,
  lastSeenStacksNotificationAtom,
  lastSeenTrialEndingModalAtom,
  notificationsLastSeenAtom,
  updateNotificationsLastSeenAtom,
} from "../atoms/notificationsLastSeen";
import { DashTrialNotificationsLastSeen } from "../service";

const pendingFetchCancellations = new Set<() => void>();

const cancelAllPendingFetches = () => {
  pendingFetchCancellations.forEach((cancel) => cancel());
  pendingFetchCancellations.clear();
};

export function useNotificationsLastSeen() {
  const initLastSeen = useSetAtom(notificationsLastSeenAtom);
  const updateLastSeenAtom = useSetAtom(updateNotificationsLastSeenAtom);
  const lastSeenChatNotification = useAtomValue(lastSeenChatNotificationAtom);
  const lastSeenOmniNotification = useAtomValue(lastSeenOmniNotificationAtom);
  const lastSeenSerpNotification = useAtomValue(lastSeenSerpNotificationAtom);
  const lastSeenStacksNotification = useAtomValue(
    lastSeenStacksNotificationAtom,
  );
  const lastSeenConnectorNotification = useAtomValue(
    lastSeenConnectorNotificationAtom,
  );
  const lastSeenTrialEndingModal = useAtomValue(lastSeenTrialEndingModalAtom);

  useEffect(() => {
    const { promise, cancel } = cancelable(getDashTrialNotificationsLastSeen());

    pendingFetchCancellations.add(cancel);

    promise
      .then((lastSeen) => {
        if (lastSeen) {
          initLastSeen(lastSeen);
        }
      })
      .catch(allowCancellations)
      .finally(() => {
        pendingFetchCancellations.delete(cancel);
      });

    return () => {
      cancel();
      pendingFetchCancellations.delete(cancel);
    };
  }, []);

  const setLastSeen = useCallback(
    (lastSeenUpdate: Partial<DashTrialNotificationsLastSeen>) => {
      cancelAllPendingFetches();
      updateLastSeenAtom(lastSeenUpdate);
    },
    [updateLastSeenAtom],
  );

  return {
    lastSeenChatNotification,
    lastSeenOmniNotification,
    lastSeenSerpNotification,
    lastSeenStacksNotification,
    lastSeenConnectorNotification,
    lastSeenTrialEndingModal,
    setLastSeen,
  };
}
